/**
 * Adaptador Vimeo (postMessage / Player.js) sin cargar su SDK.
 *
 * Vimeo publica `timeupdate` con {seconds, percent, duration}, así que no hace
 * falta reloj propio: se cachea el último estado y `probe()` lo devuelve.
 */
import { VideoSession, observeVisibility, config } from './core';

const VIMEO_EMBED = /player\.vimeo\.com\/video\/(\d+)/i;
const VIMEO_ORIGIN = 'https://player.vimeo.com';

type IframeWithState = HTMLIFrameElement & { __trkVideo?: VideoSession };

type Cached = { position: number; playing: boolean };

const players = new Map<string, { iframe: HTMLIFrameElement; session: VideoSession; cache: Cached }>();
let listening = false;
let counter = 0;

function post(iframe: HTMLIFrameElement, method: string, value?: unknown): void {
  try {
    iframe.contentWindow?.postMessage(JSON.stringify(value === undefined ? { method } : { method, value }), VIMEO_ORIGIN);
  } catch {
    /* el iframe puede no estar listo todavía */
  }
}

function listen(): void {
  if (listening || typeof window === 'undefined') return;
  listening = true;
  window.addEventListener('message', (ev: MessageEvent) => {
    if (typeof ev.origin === 'string' && ev.origin.indexOf('vimeo.com') < 0) return;
    let msg: { event?: string; method?: string; player_id?: string; data?: Record<string, number> };
    try {
      msg = typeof ev.data === 'string' ? JSON.parse(ev.data) : (ev.data as typeof msg);
    } catch {
      return;
    }
    if (!msg) return;
    const entry = msg.player_id ? players.get(String(msg.player_id)) : undefined;
    if (!entry) return;
    const { session, cache, iframe } = entry;
    const d = msg.data ?? {};

    if (msg.event === 'ready' || msg.method === 'ready') {
      for (const e of ['play', 'pause', 'ended', 'timeupdate', 'seeked', 'volumechange', 'error']) {
        post(iframe, 'addEventListener', e);
      }
      post(iframe, 'getDuration');
      session.impression();
      return;
    }
    if (msg.method === 'getDuration') {
      session.setDuration(Number((msg as { value?: number }).value) || null);
      return;
    }
    switch (msg.event) {
      case 'play':
        cache.playing = true;
        session.play(Number(d.seconds) || session.position);
        break;
      case 'pause':
        cache.playing = false;
        session.pause(Number(d.seconds) || session.position);
        break;
      case 'ended':
        cache.playing = false;
        session.ended();
        break;
      case 'seeked':
        session.seek(Number(d.seconds) || 0);
        cache.position = Number(d.seconds) || 0;
        break;
      case 'timeupdate':
        if (d.duration) session.setDuration(d.duration);
        cache.position = Number(d.seconds) || 0;
        session.tick({ position: cache.position, playing: cache.playing });
        break;
      case 'error':
        session.error('vimeo', String((d as unknown as { message?: string }).message ?? ''));
        break;
      default:
        break;
    }
  });
}

/** Instrumenta los iframes de Vimeo bajo `root`. Devuelve cuántos nuevos. */
export function scanVimeo(root: ParentNode = document): number {
  if (typeof window === 'undefined') return 0;
  const frames = Array.from(root.querySelectorAll?.('iframe') ?? []) as HTMLIFrameElement[];
  let n = 0;
  for (const f of frames) {
    if (f.hasAttribute('data-track-ignore')) continue;
    const raw = f.getAttribute('src') || '';
    const m = VIMEO_EMBED.exec(raw);
    if (!m) continue;
    if ((f as IframeWithState).__trkVideo) continue;

    const playerId = `trk${++counter}`;
    // Vimeo devuelve player_id en cada mensaje solo si va en la URL: es la
    // única forma de saber de QUÉ iframe viene el evento con varios players.
    try {
      const url = new URL(raw, location.href);
      if (url.searchParams.get('player_id') !== playerId) {
        url.searchParams.set('api', '1');
        url.searchParams.set('player_id', playerId);
        f.setAttribute('src', url.toString());
      }
    } catch {
      continue;
    }

    const session = new VideoSession({
      id: f.dataset.videoId || `vimeo:${m[1]}`,
      title: f.dataset.videoTitle || f.title || null,
      src: raw,
      player: 'vimeo',
      live: f.dataset.videoLive === 'true' || f.hasAttribute('data-video-live'),
      webinarId: f.dataset.webinarId || null,
      el: f,
    });
    (f as IframeWithState).__trkVideo = session;
    const cache: Cached = { position: 0, playing: false };
    session.probe = () => ({ position: cache.position, playing: cache.playing });
    observeVisibility(session);
    players.set(playerId, { iframe: f, session, cache });
    listen();
    // Algunos players no emiten 'ready' si ya estaban cargados: pinchamos.
    f.addEventListener('load', () => post(f, 'ping'));
    setTimeout(() => post(f, 'getDuration'), 1200);
    n += 1;
    if (config.debug) console.debug('[trackrift-video] iframe Vimeo instrumentado:', session.id);
  }
  return n;
}
